import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useStatusReportContext } from '../../statusReportContext'
import StatusReportItemSupplementalContentProvider from '../../StatusReportItemSupplementalContentProvider'
import StatusReportBoardLayout from './components/StatusReportBoardLayout'
import StatusReportDetailsPanel from './components/DetailPanel/StatusReportDetailsPanel'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    gap: 'var(--dim-4)',
    alignItems: 'flex-start',
    width: '100%'
  },
  reportSection: {
    flex: 1,
    minWidth: 0
  },
  panelWrapper: {
    position: 'sticky',
    top: 'var(--dim-2)',
    flex: '0 0 400px',
    maxWidth: '400px',
    transform: 'translateX(100%)',
    opacity: 0,
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
    '&.__visible': {
      transform: 'translateX(0)',
      opacity: 1
    }
  }
}))

function ClientStatusReportDefaultLayout (props) {
  const classes = useStyles()
  const { report, statuses, isLoading, refetch, reportExpanded, selectedStatusReportItemId, setSelectedStatusReportItemId, config } = useStatusReportContext()
  const [isVisible, setIsVisible] = useState(false)
  const useSupplementalProvider = !!(config?.supplementalDataCode || report?.hasSupplementalData)

  const selectedItem = useMemo(() => {
    if (!report?.items?.length) return null
    const findItem = (items, parent, depth = 0) => {
      for (const item of items) {
        if (item.statusReportItemId === selectedStatusReportItemId) {
          return {
            ...item,
            depth,
            parent: parent ? {
              statusReportItemId: parent.statusReportItemId,
              displayName: parent.displayName,
              statusInfo: statuses?.find(s => s.statusId === parent.status) || null
            } : undefined,
            statusInfo: statuses?.find(s => s.statusId === item.status) || null
          }
        }
        if (item?.items?.length) {
          const found = findItem(item.items, item, depth + 1)
          if (found) return found
        }
      }
      return null
    }

    return findItem(report?.items)
  }, [report?.items, statuses, selectedStatusReportItemId])

  useEffect(() => {
    if (selectedStatusReportItemId) {
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    }
  }, [selectedStatusReportItemId])

  const handleClose = () => {
    requestAnimationFrame(() => {
      setIsVisible(false)

      setTimeout(() => {
        setSelectedStatusReportItemId(undefined)
      }, 300)
    })
  }

  const content = (
    <div className={classes.container}>
      <div className={classes.reportSection}>
        <StatusReportBoardLayout
          {...props}
          loading={isLoading}
          onRefetch={refetch}
          expanded={reportExpanded}
        />
      </div>

      {selectedItem && (
        <div className={clsx(classes.panelWrapper, {
          __visible: isVisible
        })}
        >
          <StatusReportDetailsPanel
            item={selectedItem}
            config={props.config}
            onClose={handleClose}
            onUpdate={refetch}
          />
        </div>
      )}
    </div>
  )

  if (useSupplementalProvider) {
    return <StatusReportItemSupplementalContentProvider>{content}</StatusReportItemSupplementalContentProvider>
  }

  return content
}

ClientStatusReportDefaultLayout.propTypes = {
  defaultExpanded: PropTypes.bool,
  config: PropTypes.object
}

export default ClientStatusReportDefaultLayout
